/**
 * SOLID Review
 * S — Strong. One concern: what EVERY client decision returns, and what a session decision reads.
 * O — Good. A new context's changes are one more optional key here.
 * L — N/A — no inheritance hierarchy.
 * I — Good. Two schemas, built from the table's and the session's.
 * D — Good. zod and the two snapshot schemas.
 */
import { z } from 'zod';
import { ClientTable, ClientTableChanges } from './ClientTable';
import { ClientSession, ClientSessionChanges, ClientPersist } from './ClientSession';

/**
 * WHAT A DECISION CHANGES (frontend strict-ports plan, FE2, 2026-10-03). FE1's table changes, plus:
 *
 *   - `session`: the session fields that differ, each replaced whole.
 *   - `persist`: what the tab keeps — the stored game and the guest identity. The Application writes it
 *     through `ClientTabStorage` in the same step that saves the changes, so a seat taken is a game
 *     remembered, with no effect watching the store for it.
 *
 * A table decision returns the same shape; its `session` is always absent, and its `persist` says only
 * what the seat rule says (see `settle`).
 */
export const ClientChanges = ClientTableChanges.extend({
  session: ClientSessionChanges.optional(),
  persist: ClientPersist.optional(),
}).meta({ id: 'ClientChanges' });

/** A session decision's input: the table AND the session, one consistent `ClientState.snapshot`. */
export const ClientInput = z.object({ table: ClientTable, session: ClientSession }).meta({ id: 'ClientInput' });

export type IPropsClientChanges = z.infer<typeof ClientChanges>;
export type IPropsClientInput = z.infer<typeof ClientInput>;
